import { useOverviewKpis } from '../hooks/useOverviewKpis';
import { SummaryHeader } from '../../ui/SummaryHeader';
import { KpiCard } from './KpiCard';
import { DollarSign, Percent, ShoppingCart, Users } from 'lucide-react';


interface OverviewKPIsProps {
  start_date: string;
  end_date: string;
}

export function OverviewKPIs({ start_date, end_date }: OverviewKPIsProps) {
  const { data, isLoading, error } = useOverviewKpis({
    start_date,
    end_date,
  });

  if (error) {
    console.error("Error loading KPIs:", error);
    return <div>Error loading KPIs</div>;
  }


  // Ensure data[0] exists before accessing its properties
  const kpiData = data?.[0] || {};

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "N/A"; // Handle missing data
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDelta = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined) return "N/A"; // Handle missing data
    return `${rate >= 0 ? "+" : ""}${rate.toFixed(2)}%`;
  };

  const kpis = [
    {
      title: "Total Sales",
      value: formatCurrency(kpiData.total_sales?.current),
      delta: formatDelta(kpiData.total_sales?.growth_rate),
      navigateTo: "/sales",
      icon: <DollarSign className="h-4 w-4" />,
    },
    {
      title: "Avg. Order Sale",
      value: formatCurrency(kpiData.avg_order_sale?.current),
      delta: formatDelta(kpiData.avg_order_sale?.growth_rate),
      navigateTo: "/sales",
      icon: <Percent className="h-4 w-4" />,
    },
    {
      title: "New Customers",
      value: kpiData.new_customers?.current ?? "N/A", // Handle missing data
      delta: formatDelta(kpiData.new_customers?.growth_rate),
      navigateTo: "/marketing",
      icon: <Users className="h-4 w-4" />,
    },
    {
      title: "Total Orders",
      value: kpiData.orders_calculation?.total_orders ?? "N/A", // Handle missing data
      delta: formatDelta(kpiData.orders_calculation?.order_growth),
      navigateTo: "/sales",
      icon: <ShoppingCart className="h-4 w-4" />,
    },
  ];

  return (
    <div>
      <SummaryHeader title="Quick Summary" />
      <div className="grid gap-4 sm:gap-4 md:grid-cols-2 lg:grid-cols-4 w-full min-h">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} isLoading={isLoading} />
        ))}
      </div>
    </div>
  );
}
